import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/apiService';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe, ArrowLeft, KeyRound, CheckCircle, Eye, EyeOff, Shield, User, Hash } from 'lucide-react';
import Logo from './Logo';
import PasswordStrength from './PasswordStrength';
import { DarkModeToggle } from './DarkMode';
import PinLoginForm from './PinLoginForm';
import apiErrorMessage from '../lib/apiError';
import { SUBSCRIPTION_LOCK_CODE } from '../lib/sessionEvents';
import LoginNotice from './LoginNotice';
import { MIN_PASSWORD_LENGTH } from '../lib/passwordPolicy';
import { setLanguage } from '../lib/preferences';

// ── Forgot Password Views ──────────────────────────────────
const ForgotPasswordView = ({ onBack, t }) => {
  const [step, setStep] = useState(1); // 1=request, 2=reset, 3=success
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    try {
      await authApi.requestPasswordReset(username);
      toast.info(t('reset_info'));
      setStep(2);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('error')));
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!code.trim() || !newPassword) return;
    setLoading(true);
    try {
      await authApi.resetPassword(username, code, newPassword);
      setStep(3);
      toast.success(t('reset_success_msg'));
    } catch (err) {
      toast.error(apiErrorMessage(err, t('error')));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <KeyRound className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-black text-gray-900">{t('reset_title')}</h2>
      </div>

      {step === 1 && (
        <form onSubmit={handleRequest} className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">{t('reset_info')}</div>
          <div>
            <Label>{t('username')}</Label>
            <Input value={username} onChange={e => setUsername(e.target.value)} placeholder={t('username')} required className="h-11 mt-1" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-amber-700 to-orange-700 text-white font-bold rounded-xl">
            {loading ? t('loading') : t('reset_request')}
          </Button>
          <button type="button" onClick={() => setStep(2)} className="w-full text-sm text-purple-600 hover:underline">{t('reset_have_code')}</button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleReset} className="space-y-4">
          <div>
            <Label>{t('username')}</Label>
            <Input value={username} onChange={e => setUsername(e.target.value)} placeholder={t('username')} required className="h-11 mt-1" />
          </div>
          <div>
            <Label>{t('reset_code_ph')}</Label>
            <Input value={code} onChange={e => setCode(e.target.value)} placeholder="000000" maxLength={6} required className="h-11 mt-1 text-center text-xl font-mono tracking-[0.5em]" />
          </div>
          <div>
            <Label>{t('new_password')}</Label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t('new_password')} required minLength={MIN_PASSWORD_LENGTH} className="h-11 mt-1" />
            <PasswordStrength password={newPassword} />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-green-700 to-emerald-700 text-white font-bold rounded-xl">
            {loading ? t('loading') : t('reset_confirm')}
          </Button>
        </form>
      )}

      {step === 3 && (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <p className="font-bold text-green-700 text-lg">{t('reset_success_msg')}</p>
        </div>
      )}

      <button onClick={onBack} className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 pt-2">
        <ArrowLeft className="w-4 h-4" /> {t('reset_back')}
      </button>
    </div>
  );
};

// ── Main Login Component ───────────────────────────────────
const Login = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ username: '', password: '', role: 'super_admin' });
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  // [PHASE-5 2026-10] A refusal that must stay on screen (an expired school),
  // not only in a toast. The detail is an object, so it never renders raw.
  const [refusal, setRefusal] = useState('');
  // [PHASE-5 2026-10] 'password' (everyone) or 'pin' (primary/middle pupils:
  // school code + student number + 4-digit PIN).
  const [mode, setMode] = useState('password');

  const roles = [
    { value: 'super_admin',  label: t('super_admin')  },
    { value: 'school_admin', label: t('school_admin') },
    { value: 'admin_staff',  label: t('admin_staff')  },
    { value: 'teacher',      label: t('teacher')      },
    { value: 'student',      label: t('student')      },
    { value: 'parent',       label: t('parent')       },
  ];

  const languages = [
    { code: 'ar', name: t('arabic')  },
    { code: 'fr', name: t('french')  },
    { code: 'en', name: t('english') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loginAttempts >= 5) {
      toast.error(t('login_too_many'));
      return;
    }
    setLoading(true);
    setRefusal('');
    try {
      const data = await authApi.login(formData.username, formData.password, formData.role);
      setLoginAttempts(0);
      toast.success(t('success'));
      onLogin(data.access_token, data.user, data.expires_in);
    } catch (error) {
      // A 429 here is this form's to explain (App skips its global toast for
      // sign-in requests); nginx's has no JSON detail.
      const message = apiErrorMessage(error, t(error.response?.status === 429 ? 'app_rate_limited' : 'error'));
      if (error.response?.data?.detail?.code === SUBSCRIPTION_LOCK_CODE) {
        setRefusal(message);  // the password was right: not a failed attempt
      } else {
        setLoginAttempts(prev => prev + 1);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // [UI-PREFS 2026-10-31] Stores the choice; i18n sets <html lang/dir>.
  const changeLanguage = (lng) => setLanguage(lng);

  return (
    <div className="login-container">
      <div className="login-card" data-testid="login-card">
        {/* Language Switcher + Dark mode toggle */}
        <div className="flex justify-between items-center mb-4 gap-2">
          <DarkModeToggle />
          <Select value={i18n.language} onValueChange={changeLanguage}>
            <SelectTrigger className="w-32" data-testid="language-selector">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map(lang => (
                <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showForgot ? (
          <ForgotPasswordView onBack={() => setShowForgot(false)} t={t} />
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Logo
                  className="w-20 h-20 rounded-2xl"
                  style={{ boxShadow: '0 20px 60px rgba(15,118,110,0.35)' }}
                />
              </div>
              <h1 className="text-3xl font-black text-gray-900 mb-2" data-testid="login-title">
                {t('welcome_back')}
              </h1>
              <p className="text-gray-500 text-sm">{t('login_access_subtitle')}</p>
            </div>

            {/* [PHASE-5 2026-10] Why the last session ended, shown once */}
            <LoginNotice />

            {/* [PHASE-5 2026-10] Sign-in method */}
            <div className="grid grid-cols-2 gap-1 p-1 mb-5 bg-gray-100 dark:bg-gray-800 rounded-xl" role="tablist">
              {[
                { id: 'password', icon: User, label: t('pin_login_mode_password') },
                { id: 'pin', icon: Hash, label: t('pin_login_mode_pin') },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={mode === m.id}
                  onClick={() => setMode(m.id)}
                  data-testid={`login-mode-${m.id}`}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold leading-tight transition-all ${
                    mode === m.id
                      ? 'bg-white dark:bg-gray-700 text-teal-700 dark:text-teal-300 shadow'
                      : 'text-muted-foreground hover:text-gray-700'
                  }`}
                >
                  <m.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-center">{m.label}</span>
                </button>
              ))}
            </div>

            {mode === 'pin' ? (
              <PinLoginForm onLogin={onLogin} t={t} />
            ) : (
            <>
            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              {refusal && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" role="alert" data-testid="login-refusal">
                  {refusal}
                </div>
              )}
              {/* Role */}
              <div className="space-y-2">
                <Label htmlFor="role" className="text-gray-700 font-semibold text-sm">{t('select_role')}</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="role-selector" className="h-12 border-2 focus:border-teal-600 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value} data-testid={`role-${role.value}`}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700 font-semibold text-sm">{t('username')}</Label>
                <Input
                  id="username"
                  type="text"
                  data-testid="username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder={t('username')}
                  required
                  className="h-12 border-2 focus:border-teal-600 rounded-xl"
                  autoComplete="username"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold text-sm">{t('password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={t('password')}
                    required
                    className="h-12 border-2 focus:border-teal-600 rounded-xl pe-12"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Login attempts warning */}
              {loginAttempts >= 3 && loginAttempts < 5 && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Shield className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  {t('login_attempts_warning', { n: 5 - loginAttempts })}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-button"
                className="w-full h-12 bg-gradient-to-r from-teal-800 to-teal-700 hover:from-teal-900 hover:to-teal-900 text-white font-bold text-base rounded-xl transition-all hover:shadow-xl hover:shadow-teal-200 hover:scale-[1.01] active:scale-[0.99]"
                disabled={loading || loginAttempts >= 5}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('loading')}
                  </span>
                ) : t('login')}
              </Button>
            </form>

            {/* Security badge */}
            <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Shield className="w-3.5 h-3.5" />
              {t('secure_connection')}
            </div>

            {/* Forgot Password */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-sm text-teal-700 hover:text-teal-800 font-medium hover:underline transition-colors"
              >
                {t('forgot_password')}
              </button>
            </div>
            </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;